import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Scan, Check, Package, Trash2, AlertTriangle, Warehouse, RefreshCw, ClipboardList, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as validationService from '@/services/validationService';

const getErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
  }
  return detail || fallback;
};

const WMSStorageTransfer = () => {
  const navigate = useNavigate();
  const [mainTab, setMainTab] = useState('manual');

  // ---- MANUAL TRANSFER STATE ----
  const [staffId, setStaffId] = useState('');
  const [staffVerified, setStaffVerified] = useState(false);
  const [staffName, setStaffName] = useState('');
  const [scanMode, setScanMode] = useState('usn');
  const [usnCode, setUsnCode] = useState('');
  const [scannedBundles, setScannedBundles] = useState([]);
  const [bundleCode, setBundleCode] = useState('');
  const [sections, setSections] = useState([]);
  const [targetSection, setTargetSection] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const usnRef = useRef(null);
  const bundleRef = useRef(null);

  // ---- PICK SLIP STATE ----
  const [psDesigns, setPsDesigns] = useState([]);
  const [psShades, setPsShades] = useState([]);
  const [psSizes, setPsSizes] = useState([]);
  const [psDescriptions, setPsDescriptions] = useState([]);
  const [psDesign, setPsDesign] = useState('');
  const [psShade, setPsShade] = useState('');
  const [psSize, setPsSize] = useState('');
  const [psDescription, setPsDescription] = useState('');
  const [psQuantity, setPsQuantity] = useState('');
  const [psDestination, setPsDestination] = useState('');
  const [psReason, setPsReason] = useState('');
  const [psCreating, setPsCreating] = useState(false);
  const [psResult, setPsResult] = useState(null);
  const [psLoadingDropdown, setPsLoadingDropdown] = useState('');

  useEffect(() => { fetchSections(); }, []);

  // Fetch designs when pick slip tab is opened
  useEffect(() => {
    if (mainTab === 'pickslip' && psDesigns.length === 0) {
      fetchDesigns();
    }
  }, [mainTab]);

  const fetchSections = async () => {
    try {
      const response = await apiClient.get('/internal-transfer/sections', { params: { exclude_section: 'Dispatch' } });
      setSections(response.data.sections || []);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  // ---- MANUAL TRANSFER FUNCTIONS ----
  const handleStaffVerify = async () => {
    if (!staffId.trim()) { toast.error('Please enter staff ID'); return; }
    try {
      const result = await validationService.validateStaffId(staffId);
      if (result.valid) {
        setStaffVerified(true);
        setStaffName(result.staffName || result.staff?.staff_name || result.staff?.name || 'Staff');
        toast.success(`Welcome, ${result.staffName || result.staff?.staff_name || 'Staff'}`);
        setTimeout(() => {
          if (scanMode === 'usn') usnRef.current?.focus();
          else bundleRef.current?.focus();
        }, 100);
      } else {
        toast.error(result.message || 'Invalid staff ID');
      }
    } catch { toast.error('Failed to verify staff'); }
  };

  const handleScanUSN = async () => {
    if (!usnCode.trim()) return;
    const alreadyScanned = scannedBundles.find(b => b.source === 'FG Storage' && b.usn_codes?.includes(usnCode.trim().toUpperCase()));
    if (alreadyScanned) { toast.error(`USN already scanned (in bundle ${alreadyScanned.bundle_code})`); setUsnCode(''); return; }
    try {
      const response = await apiClient.post(`/internal-transfer/validate-wms-usn?usn_code=${encodeURIComponent(usnCode.trim())}`);
      const data = response.data;
      const bc = data.bundle_code;
      if (scannedBundles.find(b => b.bundle_code === bc)) {
        toast.info(`Bundle ${bc} already added`);
      } else {
        setScannedBundles(prev => [...prev, {
          bundle_code: bc, usn_count: data.bundle_usn_count || 0, bin_code: data.bin_code || '',
          sku_code: data.sku_code || '', design_name: data.design_name || '', shade: data.shade || '',
          size: data.size || '', source: 'FG Storage', loose: !!data.loose_usn
        }]);
        toast.success(data.loose_usn
          ? (data.bundle_usn_count > 1 ? `Pack ${bc} added (${data.bundle_usn_count} USNs, no bundle)` : `USN ${bc} added (no bundle, moves on its own)`)
          : `Bundle ${bc} added (${data.bundle_usn_count} USNs)`);
      }
    } catch (error) { toast.error(getErrorMessage(error, 'Failed to validate USN')); }
    setUsnCode(''); usnRef.current?.focus();
  };

  const handleScanBundle = async () => {
    if (!bundleCode.trim()) return;
    const code = bundleCode.trim().toUpperCase();
    if (scannedBundles.find(b => b.bundle_code === code)) { toast.error('Bundle already scanned'); setBundleCode(''); return; }
    try {
      const response = await apiClient.post(`/internal-transfer/validate-bundle?bundle_code=${encodeURIComponent(code)}&from_section=${encodeURIComponent('SFG Storage')}`);
      if (response.data.valid) {
        setScannedBundles(prev => [...prev, { bundle_code: code, usn_count: 0, bin_code: '', sku_code: '', design_name: '', shade: '', size: '', source: 'SFG Storage' }]);
        toast.success(`SFG Bundle ${code} added`);
      }
    } catch (error) { toast.error(getErrorMessage(error, 'Failed to validate bundle')); }
    setBundleCode(''); bundleRef.current?.focus();
  };

  const handleRemoveBundle = (bc) => { setScannedBundles(prev => prev.filter(b => b.bundle_code !== bc)); };

  const handleReset = () => {
    setStaffVerified(false); setStaffName(''); setStaffId('');
    setScannedBundles([]); setUsnCode(''); setBundleCode('');
    setTargetSection(''); setTransferReason(''); setRemarks('');
  };

  const handleSubmitTransfer = async () => {
    if (!targetSection) { toast.error('Please select a destination section'); return; }
    if (scannedBundles.length === 0) { toast.error('Please scan at least one USN or bundle'); return; }
    if (!transferReason.trim()) { toast.error('Please provide a reason for this transfer'); return; }
    const hasFG = scannedBundles.some(b => b.source === 'FG Storage');
    const hasSFG = scannedBundles.some(b => b.source === 'SFG Storage');
    if (hasFG && hasSFG) { toast.error('Cannot mix FG and SFG bundles. Create separate transfers.'); return; }
    const fromSection = hasFG ? 'FG Storage' : 'SFG Storage';
    setSubmitting(true);
    try {
      const response = await apiClient.post('/internal-transfer/create', {
        from_section: fromSection, to_section: targetSection, staff_id: staffId,
        bundle_codes: scannedBundles.map(b => b.bundle_code),
        transfer_reason: transferReason, remarks: remarks || ''
      });
      toast.success(response.data.message || 'Transfer created successfully');
      handleReset();
    } catch (error) { toast.error(getErrorMessage(error, 'Failed to create transfer')); }
    finally { setSubmitting(false); }
  };

  // ---- PICK SLIP CASCADING DROPDOWNS ----
  const fetchDesigns = async () => {
    setPsLoadingDropdown('designs');
    try {
      const res = await apiClient.get('/wms/sfg/pick-slips/dropdowns/design-names');
      setPsDesigns(res.data.design_names || []);
    } catch { toast.error('Failed to load designs'); }
    finally { setPsLoadingDropdown(''); }
  };

  const fetchShades = async (design) => {
    setPsShade(''); setPsSize(''); setPsDescription(''); setPsShades([]); setPsSizes([]); setPsDescriptions([]);
    if (!design) return;
    setPsLoadingDropdown('shades');
    try {
      const res = await apiClient.get(`/wms/sfg/pick-slips/dropdowns/shades?design_name=${encodeURIComponent(design)}`);
      setPsShades(res.data.shades || []);
    } catch { toast.error('Failed to load shades'); }
    finally { setPsLoadingDropdown(''); }
  };

  const fetchSizes = async (design, shade) => {
    setPsSize(''); setPsDescription(''); setPsSizes([]); setPsDescriptions([]);
    if (!design || !shade) return;
    setPsLoadingDropdown('sizes');
    try {
      const res = await apiClient.get(`/wms/sfg/pick-slips/dropdowns/sizes?design_name=${encodeURIComponent(design)}&shade=${encodeURIComponent(shade)}`);
      setPsSizes(res.data.sizes || []);
    } catch { toast.error('Failed to load sizes'); }
    finally { setPsLoadingDropdown(''); }
  };

  const fetchDescriptions = async (design, shade, size) => {
    setPsDescription(''); setPsDescriptions([]);
    if (!design || !shade || !size) return;
    setPsLoadingDropdown('descriptions');
    try {
      const res = await apiClient.get(`/wms/sfg/pick-slips/dropdowns/descriptions?design_name=${encodeURIComponent(design)}&shade=${encodeURIComponent(shade)}&size=${encodeURIComponent(size)}`);
      setPsDescriptions(res.data.descriptions || []);
    } catch { toast.error('Failed to load descriptions'); }
    finally { setPsLoadingDropdown(''); }
  };

  const handleCreatePickSlip = async () => {
    if (!psDesign || !psShade || !psSize || !psDescription) { toast.error('Please select all SKU fields'); return; }
    if (!psQuantity || parseInt(psQuantity) <= 0) { toast.error('Please enter a valid quantity'); return; }
    if (!psDestination) { toast.error('Please select a destination section'); return; }
    setPsCreating(true); setPsResult(null);
    try {
      const res = await apiClient.post('/wms/storage-transfer/create-pick-slip', {
        design_name: psDesign, shade: psShade, size: psSize, description: psDescription,
        quantity: parseInt(psQuantity), destination_section: psDestination,
        transfer_reason: psReason, source: 'FG',
      });
      setPsResult(res.data);
      toast.success(res.data.message || 'Pick slip created');
    } catch (error) { toast.error(getErrorMessage(error, 'Failed to create pick slip')); }
    finally { setPsCreating(false); }
  };

  const totalUSNs = scannedBundles.reduce((sum, b) => sum + (b.usn_count || 0), 0);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/wms/fg')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="wms-transfer-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">WMS Storage Transfer</h1>
              <p className="text-sm text-[#E8DCC8]">Transfer FG/SFG items or create pick slips</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        <Tabs value={mainTab} onValueChange={setMainTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="manual" data-testid="wms-transfer-manual-tab">
              <Scan className="w-4 h-4 mr-2" />
              Manual Transfer
            </TabsTrigger>
            <TabsTrigger value="pickslip" data-testid="wms-transfer-pickslip-tab">
              <ClipboardList className="w-4 h-4 mr-2" />
              Create Pick Slip
            </TabsTrigger>
          </TabsList>

          {/* ---- MANUAL TRANSFER TAB ---- */}
          <TabsContent value="manual">
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" data-testid="wms-transfer-info-banner">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Internal Transfer from WMS Storage</p>
                <p className="mt-1">This will move goods from FG/SFG storage bins to another section via the standard consignment workflow.</p>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" data-testid="wms-transfer-title">
                  <Warehouse className="w-5 h-5 text-indigo-500" />
                  Create WMS Storage Transfer
                </CardTitle>
                <CardDescription>Scan and transfer items from FG or SFG storage to another section</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {!staffVerified ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Staff ID</Label>
                      <div className="flex gap-2">
                        <Input value={staffId} onChange={(e) => setStaffId(e.target.value)} placeholder="Enter staff ID"
                          onKeyDown={(e) => e.key === 'Enter' && handleStaffVerify()} data-testid="wms-transfer-staff-input" />
                        <Button onClick={handleStaffVerify} data-testid="wms-transfer-verify-staff-btn">Verify</Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <span className="font-medium">{staffName}</span>
                        <span className="text-sm text-gray-500">({staffId})</span>
                      </div>
                      <Button variant="outline" size="sm" onClick={handleReset} data-testid="wms-transfer-change-staff-btn">Change</Button>
                    </div>

                    <Tabs value={scanMode} onValueChange={(v) => { setScanMode(v); }}>
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="usn" data-testid="wms-transfer-usn-tab"><Package className="w-4 h-4 mr-2" />USN Scan (FG)</TabsTrigger>
                        <TabsTrigger value="bundle" data-testid="wms-transfer-bundle-tab"><Scan className="w-4 h-4 mr-2" />Bundle Scan (SFG)</TabsTrigger>
                      </TabsList>
                      <TabsContent value="usn" className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label>Scan USN Barcode (FG Storage)</Label>
                          <div className="flex gap-2">
                            <Input ref={usnRef} value={usnCode} onChange={(e) => setUsnCode(e.target.value)} placeholder="Scan USN barcode from FG bin"
                              onKeyDown={(e) => e.key === 'Enter' && handleScanUSN()} data-testid="wms-transfer-usn-input" />
                            <Button onClick={handleScanUSN} variant="outline" data-testid="wms-transfer-usn-add-btn"><Scan className="w-4 h-4 mr-2" />Add</Button>
                          </div>
                          <p className="text-xs text-gray-500">Scan any USN from a bundle. The entire bundle will be transferred. A USN with no bundle is transferred on its own.</p>
                        </div>
                      </TabsContent>
                      <TabsContent value="bundle" className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label>Scan Bundle Barcode (SFG Storage)</Label>
                          <div className="flex gap-2">
                            <Input ref={bundleRef} value={bundleCode} onChange={(e) => setBundleCode(e.target.value)} placeholder="Scan bundle barcode from SFG bin"
                              onKeyDown={(e) => e.key === 'Enter' && handleScanBundle()} data-testid="wms-transfer-bundle-input" />
                            <Button onClick={handleScanBundle} variant="outline" data-testid="wms-transfer-bundle-add-btn"><Scan className="w-4 h-4 mr-2" />Add</Button>
                          </div>
                        </div>
                      </TabsContent>
                    </Tabs>

                    {scannedBundles.length > 0 && (
                      <div className="space-y-2" data-testid="wms-transfer-scanned-list">
                        <Label className="flex items-center gap-2">
                          Scanned Bundles ({scannedBundles.length})
                          {totalUSNs > 0 && <Badge variant="secondary" className="text-xs">{totalUSNs} USNs total</Badge>}
                        </Label>
                        <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                          {scannedBundles.map((bundle) => (
                            <div key={bundle.bundle_code} className="flex items-center justify-between p-3 hover:bg-gray-50">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-mono font-medium text-sm">{bundle.bundle_code}</span>
                                  <Badge variant="outline" className={bundle.source === 'FG Storage' ? 'bg-blue-50 text-blue-700 border-blue-200 text-xs' : 'bg-purple-50 text-purple-700 border-purple-200 text-xs'}>
                                    {bundle.source}
                                  </Badge>
                                  {bundle.loose && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs" data-testid={`wms-transfer-loose-${bundle.bundle_code}`}>{bundle.usn_count > 1 ? 'Loose pack · no bundle' : 'Single USN · no bundle'}</Badge>}
                                  {bundle.usn_count > 0 && <span className="text-xs text-gray-500">{bundle.usn_count} USNs</span>}
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                  {bundle.bin_code && <span>Bin: {bundle.bin_code}</span>}
                                  {bundle.design_name && <span>{bundle.design_name}</span>}
                                  {bundle.shade && <span>{bundle.shade}</span>}
                                  {bundle.size && <span>{bundle.size}</span>}
                                </div>
                              </div>
                              <button onClick={() => handleRemoveBundle(bundle.bundle_code)} className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors" data-testid={`wms-transfer-remove-${bundle.bundle_code}`}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Destination Section <span className="text-red-500">*</span></Label>
                      <Select value={targetSection} onValueChange={setTargetSection}>
                        <SelectTrigger data-testid="wms-transfer-section-select"><SelectValue placeholder="Select destination section" /></SelectTrigger>
                        <SelectContent>{sections.map((s) => <SelectItem key={s.section_name} value={s.section_name}>{s.section_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Reason for Transfer <span className="text-red-500">*</span></Label>
                      <Select value={transferReason} onValueChange={setTransferReason}>
                        <SelectTrigger data-testid="wms-transfer-reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Defective / Quality Issue">Defective / Quality Issue</SelectItem>
                          <SelectItem value="Wrong Section Allocation">Wrong Section Allocation</SelectItem>
                          <SelectItem value="Customer Return Processing">Customer Return Processing</SelectItem>
                          <SelectItem value="Design Change / Re-processing">Design Change / Re-processing</SelectItem>
                          <SelectItem value="Stock Correction">Stock Correction</SelectItem>
                          <SelectItem value="Management Decision">Management Decision</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Additional Remarks</Label>
                      <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Any additional notes..." rows={3} data-testid="wms-transfer-remarks" />
                    </div>
                    <Button onClick={handleSubmitTransfer} disabled={submitting || scannedBundles.length === 0 || !targetSection || !transferReason}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-12 text-base" data-testid="wms-transfer-submit-btn">
                      {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      {submitting ? 'Creating Transfer...' : `Create Transfer (${scannedBundles.length} bundle${scannedBundles.length !== 1 ? 's' : ''})`}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---- CREATE PICK SLIP TAB ---- */}
          <TabsContent value="pickslip">
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
              <ClipboardList className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Storage Transfer Pick Slip</p>
                <p className="mt-1">Creates a pick slip that will be assigned to FG pickers with <strong>low priority</strong>. Bundles are selected FIFO from SFG storage.</p>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" data-testid="wms-pickslip-title">
                  <ClipboardList className="w-5 h-5 text-blue-600" />
                  Create Storage Transfer Pick Slip
                </CardTitle>
                <CardDescription>Select SKU, quantity, and destination. Slip goes to the picker queue automatically.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Cascading SKU Dropdowns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Design Name <span className="text-red-500">*</span></Label>
                    <Select value={psDesign} onValueChange={(v) => { setPsDesign(v); fetchShades(v); }}>
                      <SelectTrigger data-testid="ps-design-select">
                        <SelectValue placeholder={psLoadingDropdown === 'designs' ? 'Loading...' : 'Select design'} />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {psDesigns.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Shade <span className="text-red-500">*</span></Label>
                    <Select value={psShade} onValueChange={(v) => { setPsShade(v); fetchSizes(psDesign, v); }} disabled={!psDesign || psLoadingDropdown === 'shades'}>
                      <SelectTrigger data-testid="ps-shade-select">
                        <SelectValue placeholder={psLoadingDropdown === 'shades' ? 'Loading...' : psShades.length ? 'Select shade' : 'Select design first'} />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {psShades.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Size <span className="text-red-500">*</span></Label>
                    <Select value={psSize} onValueChange={(v) => { setPsSize(v); fetchDescriptions(psDesign, psShade, v); }} disabled={!psShade || psLoadingDropdown === 'sizes'}>
                      <SelectTrigger data-testid="ps-size-select">
                        <SelectValue placeholder={psLoadingDropdown === 'sizes' ? 'Loading...' : psSizes.length ? 'Select size' : 'Select shade first'} />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {psSizes.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Description <span className="text-red-500">*</span></Label>
                    <Select value={psDescription} onValueChange={setPsDescription} disabled={!psSize || psLoadingDropdown === 'descriptions'}>
                      <SelectTrigger data-testid="ps-description-select">
                        <SelectValue placeholder={psLoadingDropdown === 'descriptions' ? 'Loading...' : psDescriptions.length ? 'Select description' : 'Select size first'} />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {psDescriptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Quantity */}
                <div className="space-y-2">
                  <Label>Quantity (pieces) <span className="text-red-500">*</span></Label>
                  <Input type="number" value={psQuantity} onChange={(e) => setPsQuantity(e.target.value)} placeholder="Number of pieces (max 120)"
                    min="1" max="120" data-testid="ps-quantity-input" />
                  <p className="text-xs text-gray-500">FG packs will be selected FIFO (oldest first) — one pack per piece. Max 120 pieces per slip.</p>
                </div>

                {/* Destination */}
                <div className="space-y-2">
                  <Label>Destination Section <span className="text-red-500">*</span></Label>
                  <Select value={psDestination} onValueChange={setPsDestination}>
                    <SelectTrigger data-testid="ps-destination-select"><SelectValue placeholder="Select destination section" /></SelectTrigger>
                    <SelectContent>
                      {sections.map((s) => <SelectItem key={s.section_name} value={s.section_name}>{s.section_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Reason */}
                <div className="space-y-2">
                  <Label>Transfer Reason</Label>
                  <Select value={psReason} onValueChange={setPsReason}>
                    <SelectTrigger data-testid="ps-reason-select"><SelectValue placeholder="Select reason (optional)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Stock Redistribution">Stock Redistribution</SelectItem>
                      <SelectItem value="Defective / Quality Issue">Defective / Quality Issue</SelectItem>
                      <SelectItem value="Design Change / Re-processing">Design Change / Re-processing</SelectItem>
                      <SelectItem value="Customer Return Processing">Customer Return Processing</SelectItem>
                      <SelectItem value="Management Decision">Management Decision</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Submit */}
                <Button onClick={handleCreatePickSlip}
                  disabled={psCreating || !psDesign || !psShade || !psSize || !psDescription || !psQuantity || !psDestination}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-base" data-testid="ps-create-btn">
                  {psCreating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardList className="w-4 h-4 mr-2" />}
                  {psCreating ? 'Creating Pick Slip...' : 'Create Pick Slip (FG Picker — Low Priority)'}
                </Button>

                {/* Result */}
                {psResult && psResult.success && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2" data-testid="ps-result">
                    <p className="text-sm font-semibold text-green-800">{psResult.message}</p>
                    <div className="text-xs text-green-700 space-y-1">
                      <p>Slip No: <span className="font-mono font-bold">{psResult.slip?.slip_no}</span></p>
                      <p>SKU: {psResult.slip?.design_name} / {psResult.slip?.shade} / {psResult.slip?.size}</p>
                      <p>Packs: {psResult.slip?.packs_count ?? psResult.slip?.bundles_count} ({psResult.slip?.actual_qty} pieces{psResult.slip?.is_partial ? ' — partial fulfillment' : ''})</p>
                      <p>Destination: {psResult.slip?.destination_section}</p>
                      <p>Priority: <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">{psResult.slip?.priority || 'Low'}</Badge></p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default WMSStorageTransfer;
